import { DeliveredStatus } from '@amzn/aet-coachingform-kit/lib/types';
import { DELIVERY_STATUS_LABEL } from '../../Table/LighthouseMetrics/displayTypes';
import { FormattedMessage } from 'react-intl';
import React from 'react';
import { ContactReviewStatusOptions } from '../../../../api/Outlier/schema';
import { IntlShape } from 'react-intl';
import { TrendIndicator } from '../Types/types';
import { ConsolidatedContact } from '../../../../api/CoachingNotes/schema';

export const DISPLAY_MAX_CONTACTS = 6;

export const coachingStatusLabels = (
  coachingStatus: DeliveredStatus
): React.ReactElement => {
  const statusLabel = DELIVERY_STATUS_LABEL[coachingStatus];
  if (statusLabel) {
    return (
      <FormattedMessage
        id={statusLabel.id}
        defaultMessage={statusLabel.default}
      />
    );
  }
  return <>--</>;
};

export const countNotReviewedContacts = (
  contacts: { contact: string; contactStatus: ContactReviewStatusOptions }[]
) => {
  if (!contacts) return 0;
  return contacts.filter(
    contact =>
      contact.contactStatus !== ContactReviewStatusOptions.REVIEWED &&
      contact.contactStatus !== ContactReviewStatusOptions.DELIVERED
  ).length;
};

export function formatDateRange(
  intl: IntlShape,
  startDate: Date,
  endDate: Date
): string {
  const formattedStartDate = intl.formatDate(startDate, {
    month: 'short',
    day: 'numeric',
  });
  const formattedEndDate = intl.formatDate(endDate, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${formattedStartDate} - ${formattedEndDate}`;
}

export function getTrendIndicator(
  deltaVal: number | string | null,
  isTimeMetric = false
): TrendIndicator {
  if (deltaVal === null) {
    return TrendIndicator.NEUTRAL;
  }
  const parsedValue =
    typeof deltaVal === 'string' ? parseFloat(deltaVal) : deltaVal;
  if (isNaN(parsedValue) && !isTimeMetric) {
    return TrendIndicator.NEUTRAL;
  }

  let sign = Math.sign(parsedValue);
  if (isTimeMetric && typeof deltaVal === 'string') {
    sign = deltaVal.startsWith('-') ? -1 : 1;
  }

  if (sign === -1) {
    return TrendIndicator.NEGATIVE;
  } else if (sign === 1) {
    return TrendIndicator.POSITIVE;
  } else {
    return TrendIndicator.NEUTRAL;
  }
}

export const adjustContactCounts = (
  contactsSummary: any,
  recomContacts: ConsolidatedContact[],
  displayIndex: number
) => {
  let notReview;
  let totalReview;

  // Count on the contact that from AssociateInsight lambda
  // for specific category and on UNDEFINED and DRAFT contacts
  const notReviewedContactCountForARC = countNotReviewedContacts(
    contactsSummary!.labelAndCount[displayIndex].contacts
  );

  // Count on the contact that both exists in Recommendation tab & AssociateInsight
  // for specific category
  const overlappedContactCount = recomContacts.filter(
    c => c.category === contactsSummary?.labelAndCount[displayIndex].category
  ).length;

  // Count on the contact that both exists in Recommendation tab & AssociateInsight
  // for specific category and on UNDEFINED and DRAFT contacts
  const notReviewedContactCountForOverlapped = recomContacts.filter(
    c =>
      c.category === contactsSummary?.labelAndCount[displayIndex].category &&
      c.contactStatus !== ContactReviewStatusOptions.REVIEWED &&
      c.contactStatus !== ContactReviewStatusOptions.DELIVERED
  ).length;

  // Count on the contact that from AssociateInsight lambda for specific category
  const arcCategoryCountInPast7Days =
    contactsSummary!.labelAndCount[displayIndex].contacts.length;

  // No Overlapped Contact
  if (overlappedContactCount === 0) {
    if (arcCategoryCountInPast7Days >= DISPLAY_MAX_CONTACTS) {
      // Scenario 1: No Overlapping Contacts, Opp/Kudo Counts >= 6
      // 1.1 Not Reviewed Contact > DISPLAY_MAX_CONTACTS, limit it to Max value of DISPLAY_MAX_CONTACTS
      // 1.2 Not Reviewed Contact < DISPLAY_MAX_CONTACTS, leave as it is
      totalReview = DISPLAY_MAX_CONTACTS;

      notReview =
        notReviewedContactCountForARC > totalReview
          ? totalReview
          : notReviewedContactCountForARC;
    } else {
      // Scenario 2: No Overlapping Contacts, Opp/Kudo Counts < DISPLAY_MAX_CONTACTS
      // 2.1 Number of contacts < DISPLAY_MAX_CONTACTS, use existing category count, and not reviewed count from ARC page
      totalReview = arcCategoryCountInPast7Days;
      notReview = notReviewedContactCountForARC;
    }
  } else {
    // Has Overlapped Contact
    // Scenario 3: Overlapped Contact > DISPLAY_MAX_CONTACTS
    // 3.1 Displayed contacts are all from overlapped contact
    if (overlappedContactCount >= DISPLAY_MAX_CONTACTS) {
      notReview = notReviewedContactCountForOverlapped;
      totalReview = overlappedContactCount;
    } else {
      // Scenario 4: Overlapped Contact < DISPLAY_MAX_CONTACTS
      // If ARC Contacts are enough to fill up to DISPLAY_MAX_CONTACTS
      // 4.1 Fill it up to DISPLAY_MAX_CONTACTS, and keep not review behaviour as ARC

      totalReview =
        arcCategoryCountInPast7Days >= DISPLAY_MAX_CONTACTS
          ? DISPLAY_MAX_CONTACTS
          : arcCategoryCountInPast7Days;
      notReview =
        notReviewedContactCountForARC > totalReview
          ? totalReview
          : notReviewedContactCountForARC;
    }
  }

  return [notReview, totalReview];
};

export function sortVerticalsAndPutTotalFirst(
  lighthouseFiltersVerticals: string[]
): string[] {
  const totalVertical = lighthouseFiltersVerticals.find(vertical =>
    vertical.startsWith('Total')
  );

  const verticalsWithoutTotal = lighthouseFiltersVerticals.filter(
    vertical => vertical !== totalVertical
  );

  return [
    ...(totalVertical ? [totalVertical] : []),
    ...new Set(verticalsWithoutTotal),
  ];
}
